import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useAuth } from "../../lib/AuthContext";
import { formatApiError } from "../../lib/api";
import { AibaLogo } from "./Logo";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User } from "lucide-react";

export const AuthModal = () => {
  const { authOpen, setAuthOpen, login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.email, form.password, form.name);
      toast.success("Bienvenue dans le Laboratoire AIBA");
      setAuthOpen(false);
      setForm({ email: "", password: "", name: "" });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent data-testid="auth-modal" className="max-w-md border-blue-500/25 bg-[#0B1220]/95 p-8 backdrop-blur-2xl">
        <DialogTitle className="sr-only">{mode === "login" ? "Connexion" : "Créer un compte"}</DialogTitle>
        <DialogDescription className="sr-only">
          {mode === "login" ? "Accédez à votre cockpit IA AIBA." : "Créez votre compte AIBA."}
        </DialogDescription>
        <div className="mb-6 flex flex-col items-center text-center">
          <AibaLogo size={40} />
          <h2 className="mt-4 font-display text-2xl font-800 tracking-tighter text-white">
            {mode === "login" ? "Connexion" : "Créer un compte"}
          </h2>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-blue-300/70">
            {mode === "login" ? "Accédez à votre cockpit IA" : "20 crédits offerts à l'inscription"}
          </p>
        </div>

        <button
          data-testid="auth-google-btn"
          onClick={loginWithGoogle}
          className="mb-5 flex w-full items-center justify-center gap-3 rounded-full border border-blue-500/25 bg-white/5 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white/10"
        >
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-4 w-4" />
          Continuer avec Google
        </button>

        <div className="mb-5 flex items-center gap-3">
          <span className="h-px flex-1 bg-blue-500/15" />
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#405060]">ou</span>
          <span className="h-px flex-1 bg-blue-500/15" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div className="flex items-center gap-3 rounded-xl border border-blue-500/20 bg-transparent px-4">
              <User size={15} className="text-blue-300" />
              <input data-testid="auth-name" value={form.name} onChange={set("name")} placeholder="Nom"
                className="w-full bg-transparent py-3 text-sm text-white outline-none placeholder:text-[#405060]" />
            </div>
          )}
          <div className="flex items-center gap-3 rounded-xl border border-blue-500/20 bg-transparent px-4">
            <Mail size={15} className="text-blue-300" />
            <input data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} placeholder="Email"
              className="w-full bg-transparent py-3 text-sm text-white outline-none placeholder:text-[#405060]" />
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-blue-500/20 bg-transparent px-4">
            <Lock size={15} className="text-blue-300" />
            <input data-testid="auth-password" type="password" required value={form.password} onChange={set("password")} placeholder="Mot de passe"
              className="w-full bg-transparent py-3 text-sm text-white outline-none placeholder:text-[#405060]" />
          </div>

          {err && <p data-testid="auth-error" className="font-body text-xs text-red-400">{err}</p>}

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:bg-blue-500 disabled:opacity-60"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {mode === "login" ? "Se connecter" : "S'inscrire"}
          </button>
        </form>

        <p className="mt-5 text-center font-body text-xs text-[#8AA0C0]">
          {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
          <button
            data-testid="auth-toggle"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
            className="font-mono uppercase tracking-[0.1em] text-blue-300 hover:text-blue-200"
          >
            {mode === "login" ? "Créer un compte" : "Se connecter"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};
